import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import { Eye, Plus, Trash2, Upload as UploadIcon, X } from 'react-feather';
import { ToolbarButton, ConfigEmptyState } from '../../components/ConfigPages';
import {
  DropZone, EtlBanner, EtlSection, EtlGrid, EtlField, EtlProgress, FilterSelect, RunStatePill,
} from '../../components/EtlPages';
import {
  uploadFileForEtl, uploadHiaGlFile, refreshCurrentOrgDatasets, getEtlConfigForHotel,
} from '../../utils/api';

const ACCEPT = '.pdf,.xlsx,.xls,.csv,.txt,.xml';
const ALLOWED = ['.pdf', '.xlsx', '.xls', '.csv', '.txt', '.xml'];
const isSupported = (name) => ALLOWED.includes(String(name).toLowerCase().slice(String(name).lastIndexOf('.')));
const mb = (bytes) => `${(bytes / 1024 / 1024).toFixed(2)} MB`;

/**
 * Accounting GL import (HIA) — a genuinely separate flow from the feed
 * uploads above: the property is detected server-side from the file's own
 * Company/Branch header, and every month in the file is replaced in full.
 * Org-gated on the `hia-gl-import` flag.
 */
function HiaGlImport() {
  const posthog = usePostHog();
  const enabled = posthog?.isFeatureEnabled('hia-gl-import') ?? false;
  const [file, setFile] = useState(null);
  const [dryRun, setDryRun] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  // Unbalanced periods the server wants confirmed before it writes anything.
  const [confirmWarnings, setConfirmWarnings] = useState(null);

  if (!enabled) return null;

  const reset = () => { setResult(null); setError(''); setConfirmWarnings(null); };

  const run = async (allowUnbalanced = false) => {
    if (!file) return;
    setBusy(true); reset();
    try {
      setResult(await uploadHiaGlFile(file, dryRun, allowUnbalanced));
    } catch (err) {
      const data = err.response?.data;
      if (data?.code === 'NEEDS_CONFIRMATION' && data.warnings?.length) setConfirmWarnings(data.warnings);
      else setError(data?.error || err.message || 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="it-frame" style={{ marginTop: 14, padding: 18 }}>
      <EtlSection
        title="Accounting GL import (HIA)"
        description='Upload an HIA "Transactions for Period" Excel export. The property is read from the file, and every month it contains is replaced in full — re-uploading the same or a newer export for a month is safe.'
      >
        {file ? (
          <div className="ep-panel" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span className="cp-name">{file.name}</span>
            <span className="cp-code">{mb(file.size)}</span>
            <div className="cp-toolbar-spacer" style={{ flex: 1 }} />
            <ToolbarButton small variant="ghost" icon={X} onClick={() => { setFile(null); reset(); }}>Remove</ToolbarButton>
          </div>
        ) : (
          <DropZone
            accept=".xlsx"
            hint="A single .xlsx export"
            onFiles={(files) => { setFile(files[0]); reset(); }}
          />
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13, color: 'var(--lodge-onyx-700)' }}>
            <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />
            Validate only — report what would change without writing
          </label>
          <div style={{ flex: 1 }} />
          <ToolbarButton variant="primary" icon={UploadIcon} onClick={() => run()} disabled={!file || busy}>
            {busy ? (dryRun ? 'Validating…' : 'Importing…') : (dryRun ? 'Validate file' : 'Import GL file')}
          </ToolbarButton>
        </div>

        {error ? <div style={{ marginTop: 12 }}><EtlBanner tone="error" title="Import failed">{error}</EtlBanner></div> : null}
        {confirmWarnings ? (
          <div style={{ marginTop: 12 }}>
            <EtlBanner
              tone="warn"
              title="This file doesn't balance"
              actions={(
                <>
                  <ToolbarButton small variant="ghost" onClick={() => setConfirmWarnings(null)} disabled={busy}>Cancel</ToolbarButton>
                  <ToolbarButton small variant="primary" onClick={() => run(true)} disabled={busy}>Import anyway</ToolbarButton>
                </>
              )}
            >
              {confirmWarnings.map((w) => w.message).join(' ')} Nothing has been written yet. Importing anyway loads the file as is, so these months will not net to zero. Are you sure?
            </EtlBanner>
          </div>
        ) : null}
        {result ? (
          <div style={{ marginTop: 12 }}>
            <EtlBanner tone="ok" title={result.dryRun ? 'File is valid' : 'Import complete'}>
              {result.location?.name} (branch {result.branch}) — {result.rows.toLocaleString()} GL lines across{' '}
              {result.periods.length} month{result.periods.length === 1 ? '' : 's'} ({result.periods[0]} → {result.periods[result.periods.length - 1]})
              {!result.dryRun && result.rowsDeleted > 0 ? ` — replaced ${result.rowsDeleted.toLocaleString()} previously imported rows` : ''}
              {!result.dryRun ? (result.warnings?.length ? '. Every imported month verified against the file.' : '. Every imported month verified balanced.') : ''}
              {!result.dryRun && result.factRefresh?.refreshed ? ' Financial reports are updated for these months.' : ''}
              {!result.dryRun && result.factRefresh?.error ? ' Report refresh failed — run a full refresh from Admin › Refresh to see these months in reports.' : ''}
            </EtlBanner>
            {result.warnings?.length ? (
              <div style={{ marginTop: 8 }}>
                <EtlBanner tone="warn" title={result.dryRun ? 'Unbalanced — importing will ask you to confirm' : 'Imported unbalanced'}>
                  {result.warnings.map((w) => w.message).join(' ')}
                </EtlBanner>
              </div>
            ) : null}
          </div>
        ) : null}
      </EtlSection>
    </div>
  );
}

/**
 * Upload — send a file through the pipeline by hand.
 *
 * Three things this fixes over the old Data Import page:
 *  · It resolves locations from the shared ETL scope (per-location access)
 *    rather than reading `localStorage.currentOrgId` and listing every
 *    location in the org, accessible or not.
 *  · The drop zone actually accepts a drop. The old one said "or drag and
 *    drop" but bound no drag handlers, so dropping a file navigated the tab
 *    to it.
 *  · "Same business date for every file" is back. The state for it existed
 *    (`globalBusinessDate` / `useGlobalDate`) but nothing rendered it, so a
 *    30-file backfill meant typing the same date thirty times.
 */
export default function UploadTab({ scope, schemaBuilderEnabled, onUploaded }) {
  const navigate = useNavigate();
  const { locationOptions, locationNameById } = scope;

  const [locationId, setLocationId] = useState(null);
  const [etlConfig, setEtlConfig] = useState(null);
  const [configError, setConfigError] = useState('');
  const [fileType, setFileType] = useState(null);
  const [files, setFiles] = useState([]); // [{ file, businessDate }]
  const [sameDate, setSameDate] = useState(true);
  const [globalDate, setGlobalDate] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(null); // { completed, total, current }
  const [outcome, setOutcome] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const mounted = useRef(true);
  const configRequestId = useRef(0);

  useEffect(() => () => { mounted.current = false; }, []);

  useEffect(() => {
    if (!locationId) { setEtlConfig(null); setFileType(null); setConfigError(''); return; }
    let alive = true;
    const requestId = ++configRequestId.current;
    (async () => {
      try {
        setConfigError('');
        const config = await getEtlConfigForHotel(locationId);
        if (!alive || configRequestId.current !== requestId) return;
        setEtlConfig(config);
        const first = config?.fileTypes?.[0]?.fileType || null;
        setFileType(first);
      } catch (err) {
        if (!alive || configRequestId.current !== requestId) return;
        console.error('Error loading ETL configuration:', err);
        setConfigError(`Failed to load the feed configuration for this location: ${err.message}`);
        setEtlConfig(null);
        setFileType(null);
      }
    })();
    return () => { alive = false; };
  }, [locationId]);

  const fileTypeOptions = useMemo(() => (etlConfig?.fileTypes || []).map((ft) => ({
    value: ft.fileType,
    label: ft.displayName ? `${ft.displayName} (${ft.fileType})` : ft.fileType,
  })), [etlConfig]);

  const addFiles = useCallback((incoming) => {
    setFiles((prev) => [...prev, ...incoming.map((file) => ({ file, businessDate: '' }))]);
    setOutcome(null);
  }, []);

  const setRowDate = (index, value) => setFiles((prev) => prev.map((row, i) => (i === index ? { ...row, businessDate: value } : row)));
  const removeRow = (index) => setFiles((prev) => prev.filter((_, i) => i !== index));

  const unsupported = files.filter((r) => !isSupported(r.file.name)).length;
  const canUpload = !!locationId && !!fileType && files.length > 0 && files.length > unsupported && !uploading;

  const clearAll = () => {
    setFiles([]); setOutcome(null); setProgress(null); setGlobalDate('');
  };

  /**
   * Report refresh after a successful import. Only fires when the org still
   * has a Power BI workspace configured — most orgs are on native reports now,
   * where this is a no-op, and a failed refresh must never mask a good upload.
   */
  const refreshReports = useCallback(async () => {
    if (!localStorage.getItem('currentOrgWorkspaceId')) return;
    setRefreshing(true);
    try {
      await refreshCurrentOrgDatasets(null, true);
    } catch (err) {
      console.warn('Report refresh after upload failed (non-fatal):', err);
    } finally {
      if (mounted.current) setRefreshing(false);
    }
  }, []);

  const upload = async () => {
    if (!canUpload) return;
    setUploading(true);
    setOutcome(null);
    setProgress({ completed: 0, total: files.length, current: null });

    const results = [];
    for (let i = 0; i < files.length; i += 1) {
      const { file, businessDate } = files[i];
      setProgress({ completed: i, total: files.length, current: file.name });
      const effectiveDate = (sameDate ? globalDate : businessDate) || null;
      try {
        const result = await uploadFileForEtl(locationId, file, fileType, effectiveDate);
        results.push({
          fileName: file.name,
          success: !!result.success,
          // Terminal failures come back as HTTP 200 with success:false and the
          // DLQ pointer in result.error — surface it, don't drop it.
          error: result.success ? null : (result.error || 'Upload failed'),
          result,
        });
      } catch (err) {
        results.push({ fileName: file.name, success: false, error: err.message, result: null });
      }
    }

    const ok = results.filter((r) => r.success).length;
    setProgress(null);
    setOutcome({
      tone: ok === results.length ? 'ok' : ok === 0 ? 'error' : 'warn',
      ok,
      failed: results.length - ok,
      results,
    });
    setFiles([]);
    setUploading(false);
    if (onUploaded) onUploaded();

    // Only refresh when rows actually landed — a queued upload hasn't inserted
    // anything yet, so refreshing now would rebuild against pre-upload data.
    if (results.some((r) => r.success && !r.result?.queued)) refreshReports();
  };

  const locationLabel = locationId ? (locationNameById.get(Number(locationId)) || locationNameById.get(locationId)) : null;

  return (
    <>
      <div className="it-frame" style={{ padding: 18 }}>
        <EtlSection title="Destination" description="Where these files land, and which feed's rules read them.">
          <EtlGrid>
            <EtlField label="Location" required>
              <FilterSelect
                width="100%"
                value={locationId}
                options={locationOptions}
                onChange={(v) => { setLocationId(v); setFiles([]); setOutcome(null); }}
                placeholder="Choose a location…"
                ariaLabel="Location"
              />
            </EtlField>
            <EtlField
              label="Feed"
              required
              help={etlConfig && !fileTypeOptions.length ? 'This location has no feeds configured yet.' : undefined}
            >
              <FilterSelect
                width="100%"
                value={fileType}
                options={fileTypeOptions}
                onChange={setFileType}
                placeholder={locationId ? 'Choose a feed…' : 'Pick a location first'}
                ariaLabel="Feed"
              />
            </EtlField>
          </EtlGrid>

          {configError ? <div style={{ marginTop: 12 }}><EtlBanner tone="error">{configError}</EtlBanner></div> : null}

          {locationId && etlConfig && !fileTypeOptions.length ? (
            <div style={{ marginTop: 12 }}>
              <EtlBanner
                tone="warn"
                title="No feeds configured"
                actions={schemaBuilderEnabled ? (
                  <ToolbarButton small icon={Plus} onClick={() => navigate(`/etl-config/schema/${locationId}`)}>Create a feed</ToolbarButton>
                ) : null}
              >
                Nothing can be processed for {locationLabel} until a feed defines how its files are recognised and read.
              </EtlBanner>
            </div>
          ) : null}
        </EtlSection>

        {locationId && fileType ? (
          <EtlSection
            title="Files"
            description="PDF, CSV, Excel, text or XML. Multiple files upload one at a time, in order."
          >
            <DropZone
              multiple
              accept={ACCEPT}
              disabled={uploading}
              hint="PDF · CSV · XLSX · XLS · TXT · XML"
              onFiles={addFiles}
            />

            {files.length ? (
              <>
                <div className="ep-panel" style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13, color: 'var(--lodge-onyx-700)' }}>
                    <input type="checkbox" checked={sameDate} onChange={(e) => setSameDate(e.target.checked)} disabled={uploading} />
                    Same business date for every file
                  </label>
                  {sameDate ? (
                    <input
                      type="date"
                      className="cp-input"
                      value={globalDate}
                      onChange={(e) => setGlobalDate(e.target.value)}
                      disabled={uploading}
                      aria-label="Business date for all files"
                      style={{ width: 168 }}
                    />
                  ) : null}
                  <span className="cp-help">Leave blank to read the date out of each file.</span>
                </div>

                <div className="it-frame" style={{ marginTop: 12 }}>
                  <table className="it-tbl">
                    <thead>
                      <tr>
                        <th>File</th>
                        <th style={{ width: 110 }}>Size</th>
                        {!sameDate ? <th style={{ width: 190 }}>Business date</th> : null}
                        <th aria-label="Actions" style={{ width: 44 }} />
                      </tr>
                    </thead>
                    <tbody>
                      {files.map((row, i) => (
                        <tr key={`${row.file.name}-${i}`} className="cp-row">
                          <td className="cp-cell">
                            <div className="cp-name">{row.file.name}</div>
                            {!isSupported(row.file.name) ? <div className="cp-code" style={{ color: 'var(--color-error-ink)' }}>Unsupported format — will be skipped</div> : null}
                          </td>
                          <td className="cp-cell"><span className="cp-mono">{mb(row.file.size)}</span></td>
                          {!sameDate ? (
                            <td className="cp-cell">
                              <input
                                type="date"
                                className="cp-input"
                                value={row.businessDate}
                                onChange={(e) => setRowDate(i, e.target.value)}
                                disabled={uploading}
                                aria-label={`Business date for ${row.file.name}`}
                                style={{ width: '100%', height: 30 }}
                              />
                            </td>
                          ) : null}
                          <td className="cp-cell-tight" style={{ textAlign: 'right' }}>
                            <span className="cp-row-actions">
                              <ToolbarButton small iconOnly variant="ghost" icon={Trash2} disabled={uploading}
                                             aria-label={`Remove ${row.file.name}`} onClick={() => removeRow(i)} />
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
                  <span className="cp-meta">
                    {files.length} file{files.length === 1 ? '' : 's'} · {mb(files.reduce((s, r) => s + r.file.size, 0))}
                    {unsupported ? ` · ${unsupported} unsupported` : ''}
                  </span>
                  <div style={{ flex: 1 }} />
                  <ToolbarButton variant="ghost" onClick={clearAll} disabled={uploading}>Clear</ToolbarButton>
                  <ToolbarButton variant="primary" icon={UploadIcon} onClick={upload} disabled={!canUpload}>
                    {uploading
                      ? `Processing ${files.length} file${files.length === 1 ? '' : 's'}…`
                      : `Upload & process ${files.length} file${files.length === 1 ? '' : 's'}`}
                  </ToolbarButton>
                </div>
              </>
            ) : null}

            {progress ? (
              <div style={{ marginTop: 14 }}>
                <EtlProgress
                  value={progress.completed}
                  total={progress.total}
                  label={`${progress.completed} / ${progress.total}`}
                />
                {progress.current ? <div className="cp-help" style={{ marginTop: 6 }}>Processing {progress.current}…</div> : null}
              </div>
            ) : null}

            {refreshing ? (
              <div style={{ marginTop: 14 }}>
                <EtlBanner tone="info" title="Refreshing reports">Rebuilding report datasets with the newly imported rows.</EtlBanner>
              </div>
            ) : null}
          </EtlSection>
        ) : null}

        {!locationId ? (
          <ConfigEmptyState
            title="Pick a location to begin"
            body="Manual uploads run through the same pipeline as the automated feeds, so the destination decides how the file is read."
          />
        ) : null}
      </div>

      {outcome ? (
        <div className="it-frame" style={{ marginTop: 14, padding: 18 }}>
          <EtlSection
            title="Result"
            actions={(
              <ToolbarButton small icon={Eye} onClick={() => navigate(`/etl-config/review?locationId=${locationId}`)}>
                Review imported data
              </ToolbarButton>
            )}
          >
            <EtlBanner tone={outcome.tone}>
              {outcome.ok} of {outcome.results.length} file{outcome.results.length === 1 ? '' : 's'} processed
              {outcome.failed ? `, ${outcome.failed} failed` : ''}.
            </EtlBanner>
            <div className="it-frame" style={{ marginTop: 12 }}>
              <table className="it-tbl">
                <thead>
                  <tr>
                    <th>File</th>
                    <th style={{ width: 150 }}>Outcome</th>
                    <th>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {outcome.results.map((r, i) => (
                    <tr key={`${r.fileName}-${i}`} className="cp-row">
                      <td className="cp-cell"><span className="cp-name">{r.fileName}</span></td>
                      <td className="cp-cell">
                        <RunStatePill status={r.success ? (r.result?.queued ? 'received' : 'normalized') : 'failed'} />
                      </td>
                      <td className="cp-cell">
                        <span style={{ fontSize: 12.5, color: r.success ? 'var(--lodge-onyx-600)' : 'var(--color-error-ink)' }}>
                          {r.success
                            ? (r.result?.queued
                              ? 'Queued — running in the background, watch it on the Status tab'
                              : `Extraction ${r.result?.formRecognizerProcessed ? 'ran' : 'skipped'} · normalization ${r.result?.normalizationCompleted ? 'complete' : 'pending'}`)
                            : r.error}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </EtlSection>
        </div>
      ) : null}

      <HiaGlImport />
    </>
  );
}
